import { t } from '@lingui/macro'
import styled from 'styled-components'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { breakpoints } from '@/config/layout'
import { ContentPagePaddingCSS } from '@/layout/components/content-page-layout/ContentPageLayout.styled'

import type { FC, ReactNode } from 'react'
import type { AppHeaderProps } from '@/@bothrs-ui/web/components/navigation/header/AppHeader.types'

type Props = {
  header?: AppHeaderProps

  children: ReactNode

  bottom?: ReactNode

  onClose?: () => void

  progress?: { min: number; max: number; value: number }
}

/**
 * Content layout including
 *  - Header with back button (optional) and close button
 *  - Content container
 *  - Footer (close button on mobile)
 */
export const CareQuestionDetailContentLayout: FC<Props> = ({ header, children, bottom, onClose, progress }) => {
  return (
    <StContainer>
      {header ? (
        <StHeader>
          <AppHeader progress={progress} {...header} />
        </StHeader>
      ) : null}

      <StContentContainer>{children}</StContentContainer>

      <StFooter>
        <StFooterContent>
          {bottom ?? (
            <Button variant='Primary' onClick={onClose}>
              {t({ id: 'globals.close', message: 'Sluiten' })}
            </Button>
          )}
        </StFooterContent>
      </StFooter>
    </StContainer>
  )
}

const StContainer = styled(FlexColumn)`
  height: 100%;
  width: 100%;
  overflow: auto;
  overflow-x: hidden;
  flex: 1;

  @media (${breakpoints.fromS}) {
    flex: unset;
    flex-shrink: 1;
    justify-content: center;
    align-items: center;

    align-self: center;
  }
`

const StHeader = styled(FlexRow)`
  flex-shrink: 1;
  justify-content: space-between;
  align-items: center;

  width: 100%;

  padding-top: ${({ theme }) => theme.UI.Spacing.Space4}px;
  padding-bottom: ${({ theme }) => theme.UI.Spacing.Space4}px;

  ${ContentPagePaddingCSS}

  @media (${breakpoints.fromS}) {
    padding-top: ${({ theme }) => theme.UI.Spacing.Space8}px;
  }
`

const StContentContainer = styled(FlexColumn)`
  flex-grow: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4}
    ${({ theme }) => theme.UI.SpacingPx.Space6};

  ${ContentPagePaddingCSS}

  @media (${breakpoints.fromS}) {
    min-width: 600px;
    padding-top: 0;
  }
`

const StFooter = styled(FlexColumn)`
  flex-shrink: 1;
  justify-content: center;
  align-items: center;
  width: 100%;

  box-shadow: 0px -1px 20px 0px #04183112;
  position: relative;
  z-index: 10;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4};

  padding-top: ${({ theme }) => theme.UI.Spacing.Space4}px;
  padding-bottom: calc(
    max(var(--safe-area-inset-bottom, 0px), 20px) +
      ${({ theme }) => theme.UI.Spacing.Space1}px
  );
  @media (${breakpoints.fromM}) {
    padding-bottom: calc(
      max(var(--safe-area-inset-bottom, 0px), 20px) +
        ${({ theme }) => theme.UI.Spacing.Space6}px
    );
  }
`

const StFooterContent = styled(FlexColumn)`
  width: 100%;
  max-width: 600px;
`
